"use client";
import React from "react";
import Link from "components/common/Link";
import Image from "components/common/Image";
import gs from "gstyles";
import { useT } from "providers/I18nProvider";

const Footer = () => {
  const t = useT();
  return (
    <div className="relative w-full">
      <div className="fixed bottom-[-314px] left-0 w-full h-[460px] bg-white z-[-1]"></div>

      {/* Dải hợp tác dành cho chủ spa/salon — nút Hợp tác trên header bị ẩn ở
          mobile, nên đây là đường vào chính cho khách doanh nghiệp. Đồng thời
          tạo internal link tới /hop-tac (tốt cho SEO).
          Mobile: gọn 1 hàng (tiêu đề + nút), mô tả chỉ hiện từ md. */}
      <section className="relative hidden md:block w-full bg-gradient-to-r from-[#eefbfb] via-[#f5fdfd] to-[#eefbfb] border-y border-[#d5f2f2]">
        <div className="container mx-auto px-4 py-3.5 md:px-6 md:py-7 flex flex-row md:flex-row items-center justify-between gap-3 md:gap-5 text-left">
          <div className="min-w-0">
            <p className="!mb-0 md:!mb-1 text-[14px] md:text-[17px] font-bold text-black01 leading-snug">
              {t("footer.partnerQuestion")}
            </p>
            <p className="!mb-0 hidden md:block text-sm text-gray-600">
              {t("footer.partnerDesc")}
            </p>
          </div>
          <Link
            href="/hop-tac"
            className="group inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2.5 md:px-6 md:py-3 text-[13px] md:text-sm font-bold text-white shadow-sm transition-all duration-200 hover:shadow-md hover:-translate-y-[1px]"
          >
            <span className="md:hidden">{t("footer.partnerCtaShort")}</span>
            <span className="hidden md:inline">{t("footer.partnerCtaFull")}</span>
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">
              →
            </span>
          </Link>
        </div>
      </section>

      <footer className="relative w-full flex flex-col bg-white py-3 md:py-4 max-md:border-t max-md:border-gray-100">
        {/* ===== Mobile (< md): gọn — 1 hàng logo + BCT, link, 1 dòng bản quyền.
            Thông tin pháp lý đầy đủ vẫn nằm trong DOM (cho đúng luật + Google), gấp trong <details>. ===== */}
        <div className="container mx-auto px-4 md:hidden">
          <div className="flex items-center justify-between gap-3">
            {gs.icons({ name: "logo-color", width: 84 })}
            <Image
              isRelative={true}
              src="/bct.png"
              alt={t("footer.bctAlt")}
              wrapClassName="relative w-[80px] aspect-[310/116] object-contain shrink-0"
              sizes="80px"
            />
          </div>
          <nav
            aria-label={t("footer.linksAriaLabel")}
            className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0 text-[12px] text-gray-500"
          >
            <Link href="/hop-tac" className="inline-block py-1">
              {t("footer.linkPartner")}
            </Link>
            <span aria-hidden>·</span>
            <Link href="/chinh-sach/quy-che-hoat-dong" className="inline-block py-1">
              {t("footer.linkOperatingRules")}
            </Link>
            <span aria-hidden>·</span>
            <Link href="/chinh-sach/bao-mat" className="inline-block py-1">
              {t("footer.linkPrivacy")}
            </Link>
          </nav>
          <details className="text-xs text-gray-400">
            <summary className="cursor-pointer list-none py-1 marker:hidden">
              {t("footer.copyright")} · <span className="underline decoration-dotted">{t("footer.legalInfo")}</span>
            </summary>
            <p className="!mb-0 pb-1 leading-relaxed">
              {t("footer.legalDetail")}
            </p>
          </details>
        </div>

        {/* ===== Desktop (≥ md): giữ nguyên bố cục đầy đủ ===== */}
        <div className="container mx-auto px-6 hidden md:flex flex-col lg:flex-row items-center lg:justify-between gap-4 lg:gap-0 flex-1">
          <div className="flex-shrink-0 mb-2">
            {gs.icons({ name: "logo-color", width: 140 })}
          </div>
          <div className="flex-1 lg:mx-8 text-sm text-gray-700 text-center lg:text-left">
            <p className="font-semibold mb-2">
              {t("footer.copyrightTitle")}
            </p>
            <p className="!mb-0">
              {t("footer.businessLicense")}
            </p>
            <p className="!mb-0 lg:mb-1">
              {t("footer.address")}
            </p>
          </div>
          <div className="flex-shrink-0 w-full max-w-[180px] lg:max-w-[230px]">
            <Image
              isRelative={true}
              src="/bct.png"
              alt={t("footer.bctAlt")}
              wrapClassName="relative w-full aspect-[310/116] rounded-md object-contain"
              sizes="200px"
            />
          </div>
        </div>
        {/* Hàng link nội bộ: giúp khách tìm nhanh, và giúp Google hiểu cấu trúc site */}
        <div className="container mx-auto px-6 pt-3 hidden md:block">
          <nav
            aria-label={t("footer.linksAriaLabel")}
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm"
          >
            <Link href="/uu-dai" className="inline-block py-2 text-gray-700 hover:text-primary">
              {t("footer.linkOffers")}
            </Link>
            <Link href="/hop-tac" className="inline-block py-2 text-gray-700 hover:text-primary">
              {t("footer.linkPartnerFull")}
            </Link>
            <Link
              href="/chinh-sach/quy-che-hoat-dong"
              className="inline-block py-2 text-gray-700 hover:text-primary"
            >
              {t("footer.linkOperatingRules")}
            </Link>
            <Link
              href="/chinh-sach/bao-mat"
              className="inline-block py-2 text-gray-700 hover:text-primary"
            >
              {t("footer.linkPrivacy")}
            </Link>
          </nav>
        </div>
        <div className="container mx-auto px-6 pt-3 hidden md:block">
          <p className="text-center text-sm text-gray-600 !mb-0">
            {t("footer.copyright")}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
